import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { API_URL, userHasPermission, userHasPanelAccess, userCanPatrol } from "../lib/utils";
import i18n from "../i18n/config";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });
          const userData = response.data;
          setUser(userData);
          setToken(storedToken);
          
          // Aplicar idioma de la empresa si está configurado
          if (userData.company_id) {
            try {
              const companyResponse = await axios.get(`${API_URL}/companies/${userData.company_id}`, {
                headers: { Authorization: `Bearer ${storedToken}` },
              });
              const company = companyResponse.data;
              if (company.preferred_language && ['es', 'en', 'fr'].includes(company.preferred_language)) {
                i18n.changeLanguage(company.preferred_language);
                localStorage.setItem('i18nextLng', company.preferred_language);
              }
            } catch (error) {
              // Silenciar errores 404 - la compañía puede no existir o el endpoint no estar disponible
              if (error.response?.status !== 404) {
                console.error("Error al obtener información de la empresa:", error);
              }
            }
          }
          
          // Si el usuario debe cambiar la contraseña y no está en la página de cambio, redirigir
          if (userData.must_change_password && window.location.pathname !== "/change-password") {
            window.location.href = "/change-password";
          }
        } catch (error) {
          console.error("Auth init error:", error);
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, {
      email,
      password,
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    
    // Aplicar idioma de la empresa si está configurado
    if (userData.company_id) {
      try {
        const companyResponse = await axios.get(`${API_URL}/companies/${userData.company_id}`, {
          headers: { Authorization: `Bearer ${newToken}` },
        });
        const company = companyResponse.data;
        if (company.preferred_language && ['es', 'en', 'fr'].includes(company.preferred_language)) {
          i18n.changeLanguage(company.preferred_language);
          localStorage.setItem('i18nextLng', company.preferred_language);
        }
      } catch (error) {
        // Silenciar errores 404 - la compañía puede no existir o el endpoint no estar disponible
        if (error.response?.status !== 404) {
          console.error("Error al obtener información de la empresa:", error);
        }
      }
    }
    
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/auth/register`, data);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  // super_admin: administrador global (todas las compañías)
  const isSuperAdmin = () => user?.role === "super_admin";
  // admin: administrador de una sola compañía (también true para super_admin)
  const isAdmin = () => user?.role === "admin" || isSuperAdmin();
  const isRevendedor = () => user?.role === "revendedor";
  const isSupervisor = () => user?.role === "supervisor";
  const isVigilante = () => user?.role === "vigilante";

  // ---- Permisos (configurables por rol desde "Roles y permisos") ----
  // El backend devuelve en login y /auth/me la lista efectiva `user.permissions`.
  const can = (perm) => userHasPermission(user, perm);
  const canAny = (perms) => perms.some((perm) => can(perm));
  // Tiene al menos un permiso del panel de gestión (si no, usa la interfaz de patrulla)
  const hasPanelAccess = () => userHasPanelAccess(user);
  const canPatrol = () => userCanPatrol(user);

  // Solo super_admin y revendedor pueden ver/elegir entre varias compañías (alcance de datos)
  const canManageCompanies = () => isSuperAdmin() || isRevendedor();
  const canManageUsers = () => can("users.manage");
  // Compatibilidad: "gestor" del módulo de visitas
  const canManage = () => can("visitors.manage");

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isSuperAdmin,
    isAdmin,
    isRevendedor,
    isSupervisor,
    isVigilante,
    can,
    canAny,
    hasPanelAccess,
    canPatrol,
    canManage,
    canManageCompanies,
    canManageUsers,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
